/** @forge-header
 * @id          activityContext
 * @role        Activity event feed context | ring buffer of 200 order/connection/error events
 * @pattern     context-provider
 * @tier        T1
 * @purity      none
 * @units       n/a
 * @upstream    none (leaf provider)
 * @downstream  ActivityRail | AppHeader | any component via useActivity()
 * @exports     ActivityProvider:component | useActivity:fn
 *              | ActivityEvent:interface | ActivityEventType:type
 * @test        activityContext.test.ts (6 cases)
 */

import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { playTone } from '@/lib/audio/audioContext';
import { readSoundSettings } from '@/lib/audio/soundSettings';

export type ActivityEventType =
  | 'order_placed'
  | 'order_filled'
  | 'order_cancelled'
  | 'queue_velocity'
  | 'liquidity_sweep'
  | 'market_trade'
  | 'connection_change'
  | 'connection_lost'
  | 'connection_restored'
  | 'queue_front'
  | 'best_price'
  | 'error';

export interface ActivityEvent {
  id: string;
  type: ActivityEventType;
  message: string;
  /** Dollar amount if applicable */
  amount?: number;
  /** Related order ID for click-to-navigate */
  orderId?: string;
  /** Related market ticker */
  ticker?: string;
  timestamp: number;
  /** MT feed source tier: self (my orders), room (room members), account (admin all) */
  source?: 'self' | 'room' | 'account';
}

export const MAX_EVENTS = 1000;
export const STORAGE_KEY = 'g1xl-activity-log-v1';

export function hydrateFromStorage(): ActivityEvent[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(0, MAX_EVENTS) as ActivityEvent[];
  } catch {
    return [];
  }
}

export function persistToStorage(events: ActivityEvent[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  } catch {
    // localStorage full or unavailable
  }
}

interface ActivityContextValue {
  events: ActivityEvent[];
  push: (event: Omit<ActivityEvent, 'id' | 'timestamp'>) => void;
  /** Merge historical events (e.g. from Kalshi API), dedup by orderId, preserve timestamps */
  hydrateRecent: (incoming: Array<Omit<ActivityEvent, 'id'>>) => void;
  clear: () => void;
}

const ActivityContext = createContext<ActivityContextValue | null>(null);

let nextId = 0;

export function ActivityProvider({ children }: { children: ReactNode }) {
  const [events, setEvents] = useState<ActivityEvent[]>(hydrateFromStorage);
  const eventsRef = useRef<ActivityEvent[]>(events);

  const push = useCallback((event: Omit<ActivityEvent, 'id' | 'timestamp'>) => {
    const full: ActivityEvent = {
      ...event,
      id: `act-${++nextId}`,
      timestamp: Date.now(),
    };

    eventsRef.current = [full, ...eventsRef.current].slice(0, MAX_EVENTS);
    setEvents(eventsRef.current);
    persistToStorage(eventsRef.current);

    // @ai-note Sound architecture: triggers distributed across 5 hooks.
    // ActivityContext.push (order_placed/cancelled), useOrderManagement.handleFill (fill priority chain),
    // useOrderManagement top-of-book (queue/best), useSweepDetection (sweep), useDefenseAlerts (threats).
    // Activity-driven sound triggers — belt-and-suspenders with direct sound paths
    const snd = readSoundSettings();
    if (snd.tradeSoundEnabled) {
      switch (event.type) {
        case 'order_placed':
          // Short ascending chirp — confirms order submission (replaces old toast)
          playTone(880, 0.08, 0.2);
          setTimeout(() => playTone(1100, 0.08, 0.2), 80);
          break;
        case 'order_cancelled':
          // Soft descending note — acknowledges cancellation
          playTone(600, 0.1, 0.15);
          break;
      }
    }
  }, []);

  const hydrateRecent = useCallback((incoming: Array<Omit<ActivityEvent, 'id'>>) => {
    if (incoming.length === 0) return;

    // Collect existing orderIds to deduplicate
    const existingOrderIds = new Set<string>();
    for (const e of eventsRef.current) {
      if (e.orderId) existingOrderIds.add(e.orderId);
    }

    const novel: ActivityEvent[] = [];
    for (const e of incoming) {
      if (e.orderId && existingOrderIds.has(e.orderId)) continue;
      if (e.orderId) existingOrderIds.add(e.orderId);
      novel.push({ ...e, id: `hist-${++nextId}` });
    }

    if (novel.length === 0) return;

    // Merge and sort newest-first, cap at MAX_EVENTS
    const merged = [...eventsRef.current, ...novel]
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, MAX_EVENTS);
    eventsRef.current = merged;
    setEvents(merged);
    persistToStorage(merged);
  }, []);

  const clear = useCallback(() => {
    eventsRef.current = [];
    setEvents([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // noop
    }
  }, []);

  return (
    <ActivityContext.Provider value={{ events, push, hydrateRecent, clear }}>
      {children}
    </ActivityContext.Provider>
  );
}

export function useActivity(): ActivityContextValue {
  const ctx = useContext(ActivityContext);
  if (!ctx) {
    throw new Error('useActivity must be used within <ActivityProvider>');
  }
  return ctx;
}
